import React, { useState, useEffect } from 'react';
import { HandMetal, Image as ImageIcon, ExternalLink, Camera, Shield, HelpCircle, Network, Rocket, Share2 } from 'lucide-react';
import boopImage from '../assets/images/boop.jpg';
import boopGif from '../assets/images/boop.gif';
import boopHowTo from '../assets/images/boop-how-to.jpg';

const HomePage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const boopSteps = [
    { title: "Approach", description: "Approach the subject to be booped with care and intention." },
    { title: "Establish Rapport", description: "For living beings, ensure they're comfortable. Inanimate objects are always ready for booping!" },
    { title: "Position", description: "Move your finger towards the target area (nose recommended)." },
    { title: "Execute the BOOP", description: "Touch gently while saying 'BOOP!' and share in the joy." }
  ];

  const memeCategories = [
    { title: "Pictures", icon: ImageIcon, description: "Static memes perfect for any situation" },
    { title: "GIFs", icon: Camera, description: "Animated goodness for maximum impact" },
    { title: "Video Clips", icon: HandMetal, description: "When you need that extra punch" }
  ];

  const faqItems = [
    { question: "What is Boop the Coin?", answer: "Boop the Coin is a fun platform combining memes with DeFi. Making finance accessible through community participation and meme sharing.", icon: HelpCircle },
    { question: "What can I do with Boop?", answer: "Trade tokens, participate in MemeFi for rewards, join community events, create and share memes.", icon: Rocket },
    { question: "How does Boop integrate with Arbitrum?", answer: "Born on Arbitrum for faster, cheaper transactions. Also available on Solana through bridged tokens and Raydium pool.", icon: Network },
    { question: "Is Boop the Coin safe?", answer: "Yes. Simple ERC-20 token with proven security. We recommend hardware wallets for maximum safety.", icon: Shield },
    { question: "How to contribute?", answer: "Create memes, join discussions, provide feedback, and help newcomers understand the platform.", icon: Share2 }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = isMobile ? faqItems.length - 1 : faqItems.length - 3;

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => prev >= maxIndex ? 0 : prev + 1);
    }, 10000);
    
    return () => clearInterval(timer);
  }, [maxIndex, activeIndex]);

  return (
    <div className="bg-gradient-to-r from-sky-50 via-sky-50/80 to-white min-h-screen pt-16 md:pt-20">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className="w-full md:w-2/5 flex justify-center md:justify-end">
            <img
              src={boopImage}
              alt="Token mascot"
              className="w-48 h-48 md:w-72 md:h-72 rounded-full object-cover shadow-lg ring-8 ring-sky-100 transform hover:scale-105 transition-all duration-300"
            />
          </div>
          <div className="w-full md:w-3/5 text-center md:text-left">
            <h1 className="text-3xl md:text-6xl font-bold text-sky-500 tracking-tight mb-8">BOOP</h1>
            <div>
              <p className="text-gray-600 text-lg md:text-xl italic font-light leading-relaxed max-w-xl">
                A gentle hit or touch on a person's or animal's nose or head,
              </p>
              <p className="text-gray-600 text-lg md:text-xl italic font-light leading-relaxed max-w-xl">
                showing that you like them or as a joke
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* About BOOP Section */}
<section className="max-w-7xl mx-auto px-4 py-8 md:py-16">
  <div className="grid md:grid-cols-2 gap-6 md:gap-8">
    {/* Left Card */}
    <div className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
      <h2 className="text-base md:text-lg font-semibold text-sky-500 mb-2">About BOOP</h2>
      <p className="text-gray-600 text-l leading-relaxed">
        Boop is not merely a word;
        <br /> <br />
        It is an incantation, a whimsical symphony that resonates with hearts across the globe.
        Its rise is marked by an infectious joy that transcends demographics, an anthem of merriment that bridges the gaps between disparate corners of the digital realm.
      </p>
    </div>
    
    {/* Right Card */}
    <div className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
      <div className="flex flex-col items-center text-center">
        <h3 className="text-base md:text-lg font-semibold text-sky-500 mb-3">Get Started with BOOP</h3>
        <p className="text-gray-600 text-xl mb-2">
          Below are a few of the FAQ's to get you started on your BOOP journey.
        </p>
        <p className="text-gray-600 text-sm mb-4">
          If you have any more questions, feel free to join our Telegram channel.
        </p>
        <a 
  href="https://t.me/boopthecoin" 
  target="_blank" 
  rel="noopener noreferrer" 
  className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg text-sm transition-colors group"
>
  <ExternalLink className="w-4 h-4 group-hover:rotate-12 transition-transform" />
  Join Our Telegram
</a>
      </div>
    </div>
  </div>
</section>

       {/* FAQ Section */}
       <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-700 ease-in-out" 
            style={{ 
              transform: `translateX(-${activeIndex * (isMobile ? 100 : 100 / 3)}%)` 
            }}
          >
            {faqItems.map((item, index) => (
              <div key={index} className={`${isMobile ? 'w-full' : 'w-1/3'} flex-shrink-0 px-2`}>
                <div className="bg-white/80 backdrop-blur-sm h-auto md:h-48 p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all flex flex-col shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <item.icon className="w-5 h-5 text-sky-500" />
                    <h3 className="text-base md:text-lg font-semibold text-sky-500">{item.question}</h3>
                  </div>
                  <div className="text-gray-600 text-sm flex-grow">{item.answer}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({ length: maxIndex + 1 }, (_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  activeIndex === i ? 'bg-sky-500 w-4' : 'bg-sky-500/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>


      {/* How to BOOP Section */}
      <section className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-sky-500">How to <span className="text-sky-500">BOOP</span></h2>
          <p className="text-gray-600 mt-4 mb-8 md:mb-12">Booping someone is meant to be playful and non-invasive.</p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
            <div className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
              <h3 className="text-xl md:text-2xl font-bold text-sky-500 mb-4 text-center">The Perfect BOOP</h3>
              <div className="aspect-video relative overflow-hidden rounded-lg">
                <img
                  src={boopHowTo}
                  alt="Perfect BOOP demonstration"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-gray-600 mt-4 text-center text-sm md:text-base">A classic example of the perfect BOOP position</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
              <h3 className="text-xl md:text-2xl font-bold text-sky-500 mb-4 text-center">BOOP in Action</h3>
              <div className="relative w-full h-48 md:h-80 overflow-hidden rounded-lg bg-gray-50 flex items-center justify-center">
                <img
                  src={boopGif}
                  alt="BOOP GIF demonstration"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-gray-600 mt-4 text-center text-sm md:text-base">Watch and learn: The perfect BOOP execution</p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto space-y-4">
          {boopSteps.map((step, index) => (
            <div key={index} className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-sky-100 flex items-center justify-center text-lg md:text-xl font-bold text-sky-500 flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-sky-500">{step.title}</h3>
                  <p className="text-gray-600 mt-2 text-sm md:text-base">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Meme Repository Section */}
      <section className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-sky-500">Meme Repository (Coming Soon)</h2>
          <p className="text-gray-600 mt-4">
            Access our HUGE collection of memes for raids or sharing with friends
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 md:mb-12">
          {memeCategories.map((category, index) => (
            <div key={index} className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
              <div className="text-center">
                <category.icon className="w-10 h-10 md:w-12 md:h-12 text-sky-500 mx-auto mb-4" />
                <h3 className="text-lg md:text-xl font-semibold text-sky-500 mb-2">{category.title}</h3>
                <p className="text-gray-600 mb-4 text-sm md:text-base">{category.description}</p>
                <button className="flex items-center justify-center gap-2 text-sky-500 hover:text-sky-600 transition-colors mx-auto">
                  <ExternalLink className="w-4 h-4" />
                  View Collection
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center bg-white/80 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-sky-200 hover:bg-white hover:border-sky-300 transition-all shadow-sm">
          <h3 className="text-xl md:text-2xl font-bold text-sky-500 mb-4">Create Your Own Memes!</h3>
          <p className="text-gray-600 mb-6 text-sm md:text-base">
            Use our MemeKit with your favorite photo editing tool like Canva.
            Just copy the boop-head into your meme and place it wherever you want!
          </p>
          <button className="bg-sky-500 hover:bg-sky-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors text-sm md:text-base">
            <Camera className="w-4 h-4 md:w-5 md:h-5" />
            Download MemeKit
          </button>
        </div>
      </section>
      <div className="w-full text-center py-4 ">
        <p className="font-serif italic text-gray-500 text-sm">
          $BOOP is a memecoin with no intrinsic value or expectation of financial return.
        </p>
      </div>
    </div>
  );
};

export default HomePage;
